import { getApiUrl, waitForBackendPort } from './api';
import { getCurrentUser } from './authApi';

const getHeaders = () => {
    const user = getCurrentUser();
    return {
        'Content-Type': 'application/json',
        'x-user-id': user?.id?.toString() || '',
        'x-user-role': user?.subscription_type || ''
    };
};

const handleResponse = async (response: Response) => {
    if (!response.ok) {
        const text = await response.text();
        console.error(`Chats API Error (${response.status}):`, text);
        return { success: false, error: `Error ${response.status}: ${text}` };
    }
    try {
        return await response.json();
    } catch (e) {
        console.error('Failed to parse JSON response:', e);
        return { success: false, error: 'invalid_json' };
    }
};

export interface WhatsAppUserContact {
    id: number;
    user_id: number;
    phone: string;
    jid: string;
    lid?: string;
    name: string;
    email: string;
    country_code: string;
    country_name: string;
    notes: string;
    tags: string[];
    metadata?: Record<string, any>;
    last_interaction_at: string;
    created_at: string;
    updated_at: string;
    last_message?: string;
    last_session_id?: string;
    unread_count?: number;
}

export interface ChatMessage {
    id: number;
    user_id: number;
    contact_id: number;
    session_id: string;
    direction: 'incoming' | 'outgoing';
    sender_type: 'customer' | 'bot' | 'agent';
    content: string;
    media_url?: string;
    media_type?: string;
    whatsapp_message_id?: string;
    status: string;
    created_at: string;
}

export const getChatContacts = async (search?: string): Promise<{ success: boolean; contacts: WhatsAppUserContact[]; error?: string }> => {
    await waitForBackendPort();
    try {
        let url = `${getApiUrl()}/api/chats/contacts`;
        if (search) {
            url += `?q=${encodeURIComponent(search)}`;
        }
        const response = await fetch(url, {
            headers: getHeaders()
        });
        return await handleResponse(response);
    } catch (error: any) {
        console.error('Network error in getChatContacts:', error);
        return { success: false, contacts: [], error: error.message };
    }
};

export const createChatContact = async (data: {
    phone: string;
    name?: string;
    email?: string;
    country_code?: string;
    country_name?: string;
    notes?: string;
    tags?: string[];
}): Promise<{ success: boolean; contact?: WhatsAppUserContact; error?: string }> => {
    await waitForBackendPort();
    try {
        const response = await fetch(`${getApiUrl()}/api/chats/contacts`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return await handleResponse(response);
    } catch (error: any) {
        console.error('Network error in createChatContact:', error);
        return { success: false, error: error.message };
    }
};

export const updateChatContact = async (
    contactId: number,
    data: Partial<WhatsAppUserContact>
): Promise<{ success: boolean; message?: string; error?: string }> => {
    await waitForBackendPort();
    try {
        const response = await fetch(`${getApiUrl()}/api/chats/contacts/${contactId}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return await handleResponse(response);
    } catch (error: any) {
        console.error('Network error in updateChatContact:', error);
        return { success: false, error: error.message };
    }
};

export const deleteChatContact = async (
    contactId: number
): Promise<{ success: boolean; message?: string; error?: string }> => {
    await waitForBackendPort();
    try {
        const response = await fetch(`${getApiUrl()}/api/chats/contacts/${contactId}`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return await handleResponse(response);
    } catch (error: any) {
        console.error('Network error in deleteChatContact:', error);
        return { success: false, error: error.message };
    }
};

export const getContactMessages = async (
    contactId: number
): Promise<{ success: boolean; messages: ChatMessage[]; error?: string }> => {
    await waitForBackendPort();
    try {
        const response = await fetch(`${getApiUrl()}/api/chats/contacts/${contactId}/messages`, {
            headers: getHeaders()
        });
        return await handleResponse(response);
    } catch (error: any) {
        console.error('Network error in getContactMessages:', error);
        return { success: false, messages: [], error: error.message };
    }
};

export const sendContactMessage = async (
    contactId: number,
    message: string,
    sessionId?: string
): Promise<{ success: boolean; message?: ChatMessage; error?: string }> => {
    await waitForBackendPort();
    try {
        const response = await fetch(`${getApiUrl()}/api/chats/contacts/${contactId}/messages`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ message, sessionId })
        });
        return await handleResponse(response);
    } catch (error: any) {
        console.error('Network error in sendContactMessage:', error);
        return { success: false, error: error.message };
    }
};
